import { Avatar, Box, Group, Stack, Text, Textarea } from '@mantine/core';
import React from 'react';
import { formatDateTime } from 'src/utils';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { InlineConfirmButton } from 'src/design/molecules/InlineConfirmButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { useAsyncAction } from 'src/design/molecules/useAsyncAction';

export interface IThreadComment {
  comment?: string;
  authorId?: number;
  author?: { fullName?: string };
  createdAt?: Date;
}

export interface ICommentInputProps<T extends IThreadComment> {
  comment: T;
  onCommentUpdate: (comment: T) => void | Promise<void>;
  onCommentDelete: (comment: T) => void | Promise<void>;
  /** Viewer's user id, used to decide edit/delete affordances. Undefined renders the comment read-only. */
  currentUserId?: number;
}

/** @registry Inline comment thread item with author/timestamp, edit-in-place textarea, and delete confirmation; edit/delete only render for the comment's own author. Generic over any DTO shaped like IThreadComment. */
export function CommentInput<T extends IThreadComment>({
  comment,
  onCommentUpdate,
  onCommentDelete,
  currentUserId,
}: ICommentInputProps<T>) {
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState(comment.comment ?? '');

  const save = useAsyncAction((next: T) => onCommentUpdate(next));

  const saveComment = async () => {
    if (value.length === 0) return;
    if (await save.run({ ...comment, comment: value })) setEditing(false);
  };

  const onFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    event.currentTarget.select();
  };

  // Plain Enter must insert a newline like any other textarea; only a
  // modifier+Enter submits, so multi-line comments remain composable.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      saveComment();
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(event.currentTarget.value);
  };

  const cancelEdit = () => {
    setValue(comment.comment ?? '');
    setEditing(false);
  };

  const isMyComment =
    currentUserId != null && currentUserId === comment.authorId;

  return (
    <Stack gap="md" mb="md">
      <Group>
        <Avatar size="sm" name={comment.author?.fullName ?? undefined} />
        <Text c="bright">{comment.author?.fullName}</Text>
        <Text>
          {formatDateTime(comment.createdAt, 'MMM Do YYYY [at] h:mm a')}
        </Text>
      </Group>
      <Box>
        {editing ? (
          <Stack gap="xs">
            <Textarea
              aria-label="Edit comment"
              autoFocus
              onKeyDown={handleKeyDown}
              onFocus={onFocus}
              onChange={handleChange}
              value={value}
              autoComplete="off"
            />
            <Group justify="flex-end" gap="xs">
              <BasicButton
                color="blue"
                disabled={value.length === 0}
                size="sm"
                variant="filled"
                onClick={saveComment}
                loading={save.loading}
              >
                Save
              </BasicButton>
              <SecondaryButton size="sm" onClick={cancelEdit}>
                Cancel
              </SecondaryButton>
            </Group>
          </Stack>
        ) : (
          <Text>{comment.comment}</Text>
        )}
      </Box>
      <Group>
        {isMyComment && (
          <>
            <BasicButton
              onClick={() => setEditing(true)}
              disabled={editing}
              key={0}
            >
              Edit
            </BasicButton>

            <InlineConfirmButton
              color="red"
              confirmButtonProps={{
                children: 'Confirm Remove',
              }}
              onConfirmClick={() => onCommentDelete(comment)}
            >
              Remove Comment
            </InlineConfirmButton>
          </>
        )}
      </Group>
    </Stack>
  );
}
